/**
 * Demo Mode Page
 * Centralized location for all demo features including role switching and data management
 */

import React, { useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './DemoModePage.css';

interface DemoRole {
  role: string;
  name: string;
  description: string;
  permissions: {
    sections: string[];
    actions: string[];
  };
}

const DemoModePage: React.FC = () => {
  const [isRoleSwitcherOpen, setIsRoleSwitcherOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string>("");
  const [seedError, setSeedError] = useState<string>("");
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState<string>("");

  // Demo queries and mutations
  const availableRoles = useQuery(api.users.getDemoRoles);
  const demoStatus = useQuery(api.users.getDemoStatus);
  const currentUser = useQuery(api.users.getUserInfo);
  const switchRole = useMutation(api.users.switchDemoRole);
  const resetToAdmin = useMutation(api.users.resetToAdmin);
  
  // Data management
  const seedBase = useAction(api.seedMockData.seedMockData as any);
  const clearAll = useMutation(api.seedMockData.clearAllData as any);

  const handleRoleSwitch = async (newRole: string) => {
    setIsLoading(true);
    try {
      const result = await switchRole({ newRole: newRole as any });
      if (result.success) {
        setIsRoleSwitcherOpen(false);
        // Refresh the page to update the UI with new permissions
        window.location.reload();
      } else {
        console.error('Failed to switch role:', result.error);
        alert(`Failed to switch role: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to switch role:', error);
      alert('Failed to switch role. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetToAdmin = async () => {
    setIsLoading(true);
    try {
      const result = await resetToAdmin();
      if (result.success) {
        setIsRoleSwitcherOpen(false);
        // Refresh the page to update the UI with new permissions
        window.location.reload();
      } else {
        console.error('Failed to reset to admin:', result.error);
        alert(`Failed to reset to admin: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to reset to admin:', error);
      alert('Failed to reset to admin. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const runSeeding = async () => {
    setSeeding(true);
    setSeedResult("");
    setSeedError("");
    try {
      // Start from a clean state
      try { await clearAll({}) } catch {}
      const baseRes: any = await seedBase({});
      const pas = baseRes?.pas || {};
      setSeedResult(`Seeded. Clients=${pas.clients ?? 0}, Policies=${pas.policies ?? 0}, Created=${pas.created ?? 0}, Updated=${pas.updated ?? 0}`);
    } catch (e: any) {
      setSeedError(e?.message || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const runClearAll = async () => {
    setClearing(true);
    setClearResult("");
    setSeedError("");
    try {
      const res = await clearAll({}) as any;
      setClearResult(`Cleared: policies=${res?.policies}, parties=${res?.parties}, claims=${res?.claims}, documents=${res?.documents}, auditLogs=${res?.auditLogs}, queue=${res?.requestQueue}`);
    } catch (e: any) {
      setSeedError(e?.message || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const currentRoleInfo = availableRoles?.find(role => role.role === demoStatus?.currentRole);
  const roleDescriptions = {
    processor: "Basic access level - Can view and create claims, upload documents",
    adjuster: "Mid-level access - Can investigate claims, assign cases, access policies", 
    manager: "Senior access - Can approve/deny claims, generate reports, manage team",
    admin: "Full system access - User management, audit logs, system configuration"
  };

  return (
    <div className="demo-mode-page">
      <div className="page-header">
        <h1>🎭 Demo Mode</h1>
        <p>Explore different access levels and manage demo data</p>
      </div>

      {/* Current Status */}
      <div className="demo-status-card">
        <h2>Current Demo Status</h2>
        <div className="status-grid">
          <div className="status-item">
            <label>Current User:</label>
            <span>{currentUser?.name || 'Loading...'}</span>
          </div>
          <div className="status-item">
            <label>Current Role:</label>
            <span className={`role-badge ${demoStatus?.currentRole}`}>
              {currentRoleInfo?.name || demoStatus?.currentRole || 'Unknown'}
            </span>
          </div>
          <div className="status-item">
            <label>Demo Mode:</label>
            <span className="demo-active">Active</span>
          </div>
        </div>
      </div>

      {/* Role Switching Section */}
      <div className="demo-section">
        <h2>Role Switching</h2>
        <p>Switch between different access levels to explore the system from various perspectives.</p>
        
        <div className="role-actions">
          <button
            className="primary-button"
            onClick={() => setIsRoleSwitcherOpen(!isRoleSwitcherOpen)}
            disabled={isLoading}
          >
            {isRoleSwitcherOpen ? 'Hide Roles' : 'Switch Role'}
          </button>
          <button
            className="danger-button"
            onClick={handleResetToAdmin}
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset to Admin'}
          </button>
        </div>

        {isRoleSwitcherOpen && (
          <div className="roles-grid">
            {availableRoles ? (
              availableRoles.map((role: DemoRole) => (
                <div 
                  key={role.role}
                  className={`role-card ${role.role === demoStatus?.currentRole ? 'current' : ''}`}
                >
                  <div className="role-header">
                    <h3>{role.name}</h3>
                    {role.role === demoStatus?.currentRole && (
                      <span className="current-badge">Current</span>
                    )}
                  </div>
                  <p className="role-description">{role.description}</p>
                  <div className="role-permissions">
                    <strong>Access:</strong> {role.permissions.sections.join(', ')}
                  </div>
                  <button
                    className={`role-switch-btn ${role.role === demoStatus?.currentRole ? 'disabled' : ''}`}
                    onClick={() => handleRoleSwitch(role.role)}
                    disabled={isLoading || role.role === demoStatus?.currentRole}
                  >
                    {role.role === demoStatus?.currentRole ? 'Current Role' : 'Switch to This Role'}
                  </button>
                </div>
              ))
            ) : (
              <div className="loading-message">Loading available roles...</div>
            )}
          </div>
        )}
      </div>

      {/* Current Permissions Display */}
      {currentUser && currentUser.permissions && (
        <div className="demo-section">
          <h2>Current Permissions</h2>
          <div className="permissions-display">
            <div className="permissions-card">
              <div className="permissions-header">
                <h3>Access Level: {currentRoleInfo?.name || currentUser.role}</h3>
                <span className={`role-badge ${currentUser.role}`}>
                  {currentUser.role.toUpperCase()}
                </span>
              </div>
              
              <div className="permissions-content">
                <div className="role-description">
                  <p>{roleDescriptions[currentUser.role as keyof typeof roleDescriptions]}</p>
                </div>

                <div className="permissions-grid">
                  <div className="permissions-section">
                    <h4>Accessible Sections</h4>
                    <div className="permissions-list">
                      {currentUser.permissions.sections.map((section: string) => (
                        <span key={section} className="permission-tag section">
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="permissions-section">
                    <h4>Available Actions</h4>
                    <div className="permissions-list">
                      {currentUser.permissions.actions.map((action: string) => (
                        <span key={action} className="permission-tag action">
                          {action.charAt(0).toUpperCase() + action.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Data Management */}
      <div className="demo-section">
        <h2>Demo Data Management</h2>
        <p>Manage the demo data for testing and demonstration purposes.</p>
        
        <div className="data-actions">
          <button
            className="primary-button"
            onClick={runSeeding}
            disabled={seeding}
          >
            {seeding ? 'Seeding...' : 'Seed Demo Data'}
          </button>
          <button
            className="secondary-button"
            onClick={runClearAll}
            disabled={clearing}
          >
            {clearing ? 'Clearing...' : 'Clear All Data'}
          </button>
        </div>

        {seedResult && (
          <div className="result-message success">
            <strong>Seed Result:</strong> {seedResult}
          </div>
        )}
        {clearResult && (
          <div className="result-message success">
            <strong>Clear Result:</strong> {clearResult}
          </div>
        )}
        {seedError && (
          <div className="result-message error">
            <strong>Error:</strong> {seedError}
          </div>
        )}
      </div>

      <style jsx>{`
        .demo-mode-page {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .page-header h1 {
          color: #333;
          margin-bottom: 0.5rem;
        }

        .page-header p {
          color: #666;
          font-size: 1.1rem;
        }

        .demo-status-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .demo-status-card h2 {
          margin: 0 0 1rem 0;
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .status-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .status-item label {
          font-weight: 600;
          opacity: 0.9;
        }

        .status-item span {
          font-size: 1.1rem;
        }

        .role-badge {
          background: rgba(255,255,255,0.2);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: bold;
          display: inline-block;
        }

        .demo-active {
          color: #4ade80;
          font-weight: bold;
        }

        .demo-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .demo-section h2 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }

        .demo-section p {
          color: #666;
          margin-bottom: 1.5rem;
        }

        .role-actions, .data-actions {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .primary-button {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .primary-button:hover {
          background: #2563eb;
        }

        .primary-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .secondary-button {
          background: #6b7280;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .secondary-button:hover {
          background: #4b5563;
        }

        .danger-button {
          background: #dc2626;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .danger-button:hover {
          background: #b91c1c;
        }

        .roles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .role-card {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          transition: border-color 0.2s;
        }

        .role-card:hover {
          border-color: #3b82f6;
        }

        .role-card.current {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .role-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .role-header h3 {
          margin: 0;
          color: #333;
        }

        .current-badge {
          background: #10b981;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .role-description {
          color: #666;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }

        .role-permissions {
          font-size: 0.9rem;
          color: #888;
          margin-bottom: 1rem;
        }

        .role-switch-btn {
          width: 100%;
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .role-switch-btn:hover {
          background: #2563eb;
        }

        .role-switch-btn.disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .permissions-display {
          margin-top: 1rem;
        }

        .permissions-card {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .permissions-header {
          background: #f8f9fa;
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .permissions-header h3 {
          margin: 0;
          color: #333;
        }

        .permissions-content {
          padding: 1rem;
        }

        .role-description {
          background: #f8f9fa;
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
          border-left: 4px solid #3b82f6;
        }

        .permissions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        @media (max-width: 768px) {
          .permissions-grid {
            grid-template-columns: 1fr;
          }
        }

        .permissions-section h4 {
          margin: 0 0 0.75rem 0;
          color: #333;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .permissions-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .permission-tag {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .permission-tag.section {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .permission-tag.action {
          background: #fce7f3;
          color: #be185d;
        }

        .result-message {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 6px;
        }

        .result-message.success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .result-message.error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        .loading-message {
          text-align: center;
          padding: 2rem;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default DemoModePage;